<template>
    <div ref="wrapper" class="rich-text-editor" :style="{ border: '1px solid #ccc', zIndex: 100 }"></div>
</template>

<script>
/* global window */
import { createUEConfig, createToolbarConfig } from '@/utils/editorConfig'

let instanceCounter = 0
let ueditorLoaded = false
let ueditorLoadCallbacks = []

// ============================================================
// 补丁 1：修复 UETable.getMaxRows/getMaxCols 在 this.table 不是 <table> 元素时崩溃
// 错误：Cannot read properties of undefined (reading '0') at UE.UETable.getMaxRows
// 根源：UETable 构造函数调用 this.update(table) → this.getMaxRows() → this.table.rows（为 undefined）
// ============================================================
function patchUEditor() {
    if (!window.UE || !window.UE.UETable) return
    var proto = window.UE.UETable.prototype
    if (proto.__hbxcPatched) return
    proto.__hbxcPatched = true

    proto.getMaxRows = function () {
        var rows = this.table && this.table.rows
        if (!rows || !rows.length) return 1
        var maxLen = 1
        for (var i = 0, row; row = rows[i]; i++) {
            var currentMax = 1
            for (var j = 0, cj; cj = row.cells[j++];) {
                currentMax = Math.max(cj.rowSpan || 1, currentMax)
            }
            maxLen = Math.max(currentMax + i, maxLen)
        }
        return maxLen
    }

    proto.getMaxCols = function () {
        var rows = this.table && this.table.rows
        if (!rows || !rows.length) return 1
        var maxLen = 0, cellRows = {}
        for (var i = 0, row; row = rows[i]; i++) {
            var cellsNum = 0
            for (var j = 0, cj; cj = row.cells[j++];) {
                cellsNum += (cj.colSpan || 1)
                if (cj.rowSpan && cj.rowSpan > 1) {
                    for (var k = 1; k < cj.rowSpan; k++) {
                        if (!cellRows['row_' + (i + k)]) {
                            cellRows['row_' + (i + k)] = (cj.colSpan || 1)
                        } else {
                            cellRows['row_' + (i + k)]++
                        }
                    }
                }
            }
            cellsNum += cellRows['row_' + i] || 0
            maxLen = Math.max(cellsNum, maxLen)
        }
        return maxLen
    }
}

// ============================================================
// 清洗 HTML：纯字符串级别移除 file:// 图片 + Office/WPS 残渣标签
// 注意：绝不用 innerHTML 解析含 file:// 的 HTML（浏览器会同步尝试加载图片导致卡死）
// ============================================================
var FILE_URL_RE = /file:\/\/[^"'<>\s]+/gi
function sanitizeContentForUEditor(html) {
    if (!html) return html
    try {
        // 移除 <img ... src="file://..." ...>
        html = html.replace(/<img[^>]*>/gi, function (match) {
            return /^file:/i.test(match.replace(/\s+/g, ' ').replace(/src\s*=\s*["']?([^"'\s>]+)/i, function (_, src) {
                return src
            })) ? '' : match
        })

        // 移除所有包含 file:// 的属性值（背景图、href 等）
        html = html.replace(/(\s+(?:style|src|href|background|data|sizes)=["'])([^"']*file:\/\/[^"']*)(["'])/gi, function (_, prefix, val, suffix) {
            return /^file:/i.test(val) ? '' : prefix + val + suffix
        })

        // 移除 <o:p> <v:*> <w:*> <win:*> 等 Office/VML 命名空间标签
        html = html.replace(/<(o:|v:|w:|win:)[^>]*>[\s\S]*?<\/\1[^>]*>/gi, '')
        html = html.replace(/<(o:|v:|w:|win:)[^>]*\/?>/gi, '')
        html = html.replace(/<o:p[^>]*>/gi, '')

        return html
    } catch (e) {
        return html
    }
}

// ============================================================
// 加载 UEditor 资源（只加载一次，批量回调）
// ============================================================
function loadUEditorResources(callback) {
    if (ueditorLoaded && window.UE && window.UE.getEditor) {
        callback()
        return
    }
    ueditorLoadCallbacks.push(callback)
    if (ueditorLoadCallbacks.length > 1) return

    var cssFiles = [
        '/百度富文本编辑器ueditor/ueditor-master/themes/default/css/ueditor.css',
        '/百度富文本编辑器ueditor/ueditor-master/themes/default/css/ueditor.min.css'
    ]
    cssFiles.forEach(function (href) {
        var link = document.createElement('link')
        link.rel = 'stylesheet'
        link.type = 'text/css'
        link.href = href
        document.getElementsByTagName('head')[0].appendChild(link)
    })

    var configScript = document.createElement('script')
    configScript.type = 'text/javascript'
    configScript.src = '/百度富文本编辑器ueditor/ueditor-master/ueditor.config.js'
    configScript.onerror = function () {
        console.error('加载 ueditor.config.js 失败')
        ueditorLoadCallbacks.forEach(function (cb) { cb() })
        ueditorLoadCallbacks = []
    }
    configScript.onload = function () {
        var coreScript = document.createElement('script')
        coreScript.type = 'text/javascript'
        coreScript.src = '/百度富文本编辑器ueditor/ueditor-master/ueditor.all.min.js'
        coreScript.onerror = function () {
            console.error('加载 ueditor.all.min.js 失败')
            ueditorLoadCallbacks.forEach(function (cb) { cb() })
            ueditorLoadCallbacks = []
        }
        coreScript.onload = function () {
            ueditorLoaded = true
            ueditorLoadCallbacks.forEach(function (cb) { cb() })
            ueditorLoadCallbacks = []
        }
        document.getElementsByTagName('head')[0].appendChild(coreScript)
    }
    document.getElementsByTagName('head')[0].appendChild(configScript)
}

// ============================================================
// 组件
// ============================================================
export default {
    name: 'RichTextEditor',
    props: {
        value: { type: String, default: '' },
        height: { type: Number, default: 300 },
        placeholder: { type: String, default: '请输入内容...' },
        toolbarType: { type: String, default: 'default' },
        customToolbarKeys: { type: Array, default: null },
        mode: { type: String, default: 'default' },
        disabled: { type: Boolean, default: false },
        uploadConfig: { type: Object, default: () => ({}) }
    },
    data() {
        return {
            editorInstance: null,
            instanceId: `ueditor-${++instanceCounter}`,
            isDestroying: false,
            pendingContent: null,
            _pasteHandler: null
        }
    },
    computed: {
        editorConfig() {
            const toolbarKeys = this.customToolbarKeys
                ? this.customToolbarKeys
                : createToolbarConfig(this.toolbarType)
            const baseConfig = {
                placeholder: this.placeholder,
                initialFrameHeight: this.height,
                toolbars: toolbarKeys,
                readonly: this.disabled,
                autoClearinitialContent: false,
                enableAutoSave: false,
                saveInterval: 500,
                wordCount: false,
                elementPathEnabled: false,
                enableContextMenu: true,
                autoSyncData: false,
                pasteplain: false
            }
            return createUEConfig({ ...baseConfig, ...this.uploadConfig })
        }
    },
    watch: {
        value(newVal) {
            if (!this.isDestroying && newVal !== this.getContent()) {
                if (this.editorInstance) {
                    this.editorInstance.setContent(sanitizeContentForUEditor(newVal))
                } else {
                    this.pendingContent = newVal
                }
            }
        },
        disabled(newVal) {
            if (this.editorInstance && !this.isDestroying) {
                try {
                    if (newVal) {
                        this.editorInstance.setDisabled()
                    } else {
                        this.editorInstance.setEnabled()
                    }
                } catch (e) {
                    console.warn('切换富文本编辑器禁用状态失败:', e)
                }
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this._initEditor()
        })
    },
    beforeDestroy() {
        this.isDestroying = true
        var wrapper = this.$refs.wrapper
        if (this._pasteHandler && wrapper) {
            try { wrapper.removeEventListener('paste', this._pasteHandler, true) } catch (e) { /* ignore */ }
            this._pasteHandler = null
        }
        var scriptTag = document.getElementById(this.instanceId)
        if (scriptTag && wrapper) {
            try { wrapper.removeChild(scriptTag) } catch (e) { /* ignore */ }
        }
        if (this.editorInstance) {
            try { this.editorInstance.blur() } catch (e) { /* ignore */ }
            try { this.editorInstance.destroy() } catch (e) {
                console.warn('富文本编辑器销毁失败:', e)
            }
            this.editorInstance = null
        }
    },
    methods: {
        _initEditor() {
            var self = this
            var wrapper = self.$refs.wrapper
            if (!wrapper) {
                console.warn('UEditor 容器未找到')
                return
            }

            // ── 拦截层 1：document 级别，在 UEditor 处理之前清洗剪贴板中的 file:// ──
            var pastedHandler = function (e) {
                var clipboardData = e.clipboardData
                    || (window.clipboardData ? window.clipboardData : null)
                if (!clipboardData) return
                var rawHTML = clipboardData.getData ? clipboardData.getData('text/html') : ''
                if (!rawHTML || !/file:\/\//i.test(rawHTML)) return
                e.preventDefault ? e.preventDefault() : (e.returnValue = false)
                var sanitized = sanitizeContentForUEditor(rawHTML)
                if (window.UE && self.editorInstance) {
                    try { self.editorInstance.execCommand('insertHtml', sanitized) } catch (err) { /* ignore */ }
                }
                return false
            }
            wrapper.addEventListener('paste', pastedHandler, true)
            this._pasteHandler = pastedHandler

            loadUEditorResources(function () {
                if (self.isDestroying) return

                // 应用补丁
                patchUEditor()

                setTimeout(function () {
                    if (self.isDestroying) return

                    // 清理旧的 script 标签
                    var old = document.getElementById(self.instanceId)
                    if (old && old.parentNode) old.parentNode.removeChild(old)

                    // 创建 UEditor 所需的 script 标签
                    var scriptTag = document.createElement('script')
                    scriptTag.id = self.instanceId
                    scriptTag.name = 'content'
                    scriptTag.type = 'text/plain'
                    wrapper.appendChild(scriptTag)

                    var config = {
                        ...self.editorConfig,
                        UEDITOR_HOME_URL: '/百度富文本编辑器ueditor/ueditor-master/',
                        serverUrl: self.editorConfig.serverUrl || ''
                    }

                    var editor
                    try {
                        editor = window.UE.getEditor(self.instanceId, config)
                    } catch (initErr) {
                        console.error('UEditor 初始化失败:', initErr)
                        return
                    }

                    editor.addListener('ready', function () {
                        if (self.isDestroying) {
                            try { editor.destroy() } catch (e) { /* ignore */ }
                            return
                        }

                        // 再次应用补丁（ready 阶段可能重新挂载原型）
                        patchUEditor()

                        self.editorInstance = editor

                        // 清洗后设置内容
                        if (self.pendingContent) {
                            editor.setContent(sanitizeContentForUEditor(self.pendingContent))
                            self.pendingContent = null
                        } else if (self.value) {
                            editor.setContent(sanitizeContentForUEditor(self.value))
                        }

                        // 内容变更时通知父组件
                        editor.addListener('contentChange', function () {
                            if (!self.isDestroying) {
                                self.$emit('input', editor.getContent())
                            }
                        })

                        if (self.disabled) {
                            try { editor.setDisabled() } catch (e) {
                                console.warn('初始化富文本编辑器禁用状态失败:', e)
                            }
                        }
                    })
                }, 100)
            })
        },
        clear() {
            if (this.editorInstance) {
                try { this.editorInstance.setContent('') } catch (e) {
                    console.warn('UEditor clear failed:', e)
                }
            }
        },
        getContent() {
            if (this.editorInstance) {
                try { return this.editorInstance.getContent() } catch (e) {
                    return this.value
                }
            }
            return this.value
        },
        setContent(content) {
            if (this.editorInstance) {
                try { this.editorInstance.setContent(content) } catch (e) {
                    console.warn('UEditor setContent failed:', e)
                }
            }
            this.pendingContent = content
        },
        insertContent(content) {
            if (this.editorInstance) {
                try { this.editorInstance.execCommand('insertHtml', content) } catch (e) {
                    console.warn('UEditor insertContent failed:', e)
                }
            }
        },
        getText() {
            if (this.editorInstance) {
                try { return this.editorInstance.getContentTxt() } catch (e) {
                    return ''
                }
            }
            return ''
        }
    }
}
</script>

<style lang="less" scoped>
.rich-text-editor {
    border-radius: 4px;
    line-height: normal;
    max-height: 45vh;
    overflow-y: auto;
}
</style>

<style>

/* ── 基础排版：段落 ── */
.edui-editor-body .edui-body-container {
    font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', 'SimSun', serif;
    font-size: 16px;
    line-height: 1.8;
    color: #333;
    letter-spacing: 0.5px;
    padding: 16px 20px;
}

.edui-editor-body .edui-body-container p {
    margin: 0;
    margin-bottom: 12px;
    line-height: 1.8;
    text-indent: 2em;
}

.edui-editor-body .edui-body-container p:first-child {
    text-indent: 2em;
}

.edui-editor-body .edui-body-container h1 {
    font-size: 26px;
    font-weight: 700;
    margin: 0;
    margin-bottom: 16px;
    line-height: 1.4;
    text-indent: 0;
    color: #1a1a1a;
}
.edui-editor-body .edui-body-container h2 {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
    margin-bottom: 14px;
    line-height: 1.4;
    text-indent: 0;
    color: #1a1a1a;
}
.edui-editor-body .edui-body-container h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    margin-bottom: 12px;
    line-height: 1.5;
    text-indent: 0;
    color: #1a1a1a;
}
.edui-editor-body .edui-body-container h4,
.edui-editor-body .edui-body-container h5,
.edui-editor-body .edui-body-container h6 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    margin-bottom: 10px;
    line-height: 1.5;
    text-indent: 0;
    color: #1a1a1a;
}

/* ── 列表 ── */
.edui-editor-body .edui-body-container ul,
.edui-editor-body .edui-body-container ol {
    margin: 0;
    margin-bottom: 12px;
    padding-left: 2.5em;
    line-height: 1.8;
}
.edui-editor-body .edui-body-container ul {
    list-style-type: disc;
}
.edui-editor-body .edui-body-container ol {
    list-style-type: decimal;
}
.edui-editor-body .edui-body-container li {
    margin: 4px 0;
    line-height: 1.8;
    padding-left: 4px;
    text-indent: 0;
}
.edui-editor-body .edui-body-container li p {
    margin: 0;
    text-indent: 0;
}

/* ── 块引用 ── */
.edui-editor-body .edui-body-container blockquote {
    margin: 0;
    margin-bottom: 12px;
    padding: 8px 16px;
    border-left: 4px solid #c0c0c0;
    background: #f8f8f8;
    color: #555;
    text-indent: 0;
}

/* ── 代码 ── */
.edui-editor-body .edui-body-container pre {
    margin: 0;
    margin-bottom: 12px;
    padding: 12px 16px;
    background: #f5f5f5;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    font-family: 'Courier New', Consolas, monospace;
    font-size: 14px;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-all;
    text-indent: 0;
}
.edui-editor-body .edui-body-container code {
    padding: 2px 6px;
    background: #f0f0f0;
    border-radius: 3px;
    font-family: 'Courier New', Consolas, monospace;
    font-size: 14px;
    color: #c7254e;
}

/* ── 分割线 ── */
.edui-editor-body .edui-body-container hr {
    margin: 16px 0;
    border: none;
    border-top: 1px solid #d0d0d0;
}

/* ── 链接 ── */
.edui-editor-body .edui-body-container a {
    color: #2a5caa;
    text-decoration: underline;
}

/* ── 表格 ── */
.edui-editor-body table {
    border-collapse: collapse;
    width: 100% !important;
    table-layout: auto !important;
    margin: 0;
    margin-bottom: 12px;
    font-size: 14px;
}
.edui-editor-body table col,
.edui-editor-body table colgroup col {
    width: auto !important;
}
.edui-editor-body table td,
.edui-editor-body table th {
    border: 1px solid #a0a0a0;
    padding: 6px 10px;
    box-sizing: border-box;
    vertical-align: middle;
    width: auto !important;
    min-width: 40px;
    word-break: break-word;
    text-indent: 0;
    background: #fff;
}
.edui-editor-body table th {
    background: #f0f0f0;
    font-weight: 600;
    text-align: center;
}

/* ── 图片 ── */
.edui-editor-body .edui-body-container img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 8px auto;
    border-radius: 2px;
}

/* ── Word 粘贴残留样式清理 ── */
.edui-editor-body .edui-body-container span[style*="mso-"],
.edui-editor-body .edui-body-container div[style*="mso-"],
.edui-editor-body .edui-body-container p[style*="mso-"],
.edui-editor-body .edui-body-container font,
.edui-editor-body .edui-body-container o\:p {
    font-size: inherit !important;
    font-family: inherit !important;
    color: inherit !important;
    letter-spacing: inherit !important;
    line-height: inherit !important;
    vertical-align: baseline !important;
}
</style>
